/**
 * Role Management Page - User Access Management Module
 * Created: 2024-11-12
 * Purpose: Manage custom roles and permissions
 * Part of: User Access Management Module Implementation - Phase 4.2
 */

import React, { useState, useEffect } from 'react';
import {
  Plus,
  Pencil,
  Trash2,
  Search,
  Copy,
  Shield,
  ShieldCheck,
  Users,
  CheckCircle,
  Loader2,
  Lock,
  ChevronRight,
  Info,
  MessageSquare,
  BarChart3,
  Zap,
  Mail,
  Bell,
  FileText,
  Database,
  Globe,
  Key,
  Activity,
  Settings2,
  UserCircle2,
  CalendarDays,
  Hash,
} from 'lucide-react';
import { customRolesAPI, permissionsAPI } from '../services/api';
import PermissionMatrixDialog from '../components/RoleManagement/PermissionMatrixDialog';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Card, CardContent } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { toast } from '../components/ui/use-toast';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { cn } from '../lib/utils';

// ============================================================================
// INTERFACES
// ============================================================================

interface CustomRole {
  id: number;
  account_id: number;
  name: string;
  description: string | null;
  is_default: boolean;
  is_active: boolean;
  permission_count: number;
  user_count: number;
  created_at: string;
  updated_at: string;
}

interface PermissionCategory {
  category: string;
  category_display_name: string;
  permissions: any[];
}

interface RoleFormData {
  name: string;
  description: string;
  is_active: boolean;
}

// ============================================================================
// HELPERS
// ============================================================================

const AVATAR_COLORS = [
  'bg-violet-100 text-violet-700 dark:bg-violet-950 dark:text-violet-300',
  'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300',
  'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300',
  'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300',
  'bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-300',
  'bg-cyan-100 text-cyan-700 dark:bg-cyan-950 dark:text-cyan-300',
  'bg-orange-100 text-orange-700 dark:bg-orange-950 dark:text-orange-300',
  'bg-indigo-100 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300',
];

const getAvatarColor = (name: string): string => {
  const hash = name.split('').reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
};

const getRoleInitials = (name: string): string => {
  const words = name.trim().split(/\s+/);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
};

const CATEGORY_ICON_MAP: Array<[string, React.ElementType]> = [
  ['conversation', MessageSquare],
  ['contact', UserCircle2],
  ['agent', Users],
  ['report', BarChart3],
  ['analytic', BarChart3],
  ['automation', Zap],
  ['email', Mail],
  ['notification', Bell],
  ['template', FileText],
  ['knowledge', Database],
  ['channel', Globe],
  ['access', Key],
  ['activity', Activity],
  ['setting', Settings2],
  ['permission', Key],
  ['role', Shield],
  ['label', Hash],
];

const getCategoryIcon = (category: string): React.ElementType => {
  const lower = category.toLowerCase();
  for (const [key, Icon] of CATEGORY_ICON_MAP) {
    if (lower.includes(key)) return Icon;
  }
  return Shield;
};

const CATEGORY_PALETTE = [
  'bg-violet-50 border-violet-200 text-violet-700 dark:bg-violet-950/40 dark:border-violet-800/50 dark:text-violet-300',
  'bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-950/40 dark:border-blue-800/50 dark:text-blue-300',
  'bg-emerald-50 border-emerald-200 text-emerald-700 dark:bg-emerald-950/40 dark:border-emerald-800/50 dark:text-emerald-300',
  'bg-amber-50 border-amber-200 text-amber-700 dark:bg-amber-950/40 dark:border-amber-800/50 dark:text-amber-300',
  'bg-rose-50 border-rose-200 text-rose-700 dark:bg-rose-950/40 dark:border-rose-800/50 dark:text-rose-300',
  'bg-cyan-50 border-cyan-200 text-cyan-700 dark:bg-cyan-950/40 dark:border-cyan-800/50 dark:text-cyan-300',
  'bg-orange-50 border-orange-200 text-orange-700 dark:bg-orange-950/40 dark:border-orange-800/50 dark:text-orange-300',
  'bg-indigo-50 border-indigo-200 text-indigo-700 dark:bg-indigo-950/40 dark:border-indigo-800/50 dark:text-indigo-300',
];

const getCategoryPalette = (index: number) => CATEGORY_PALETTE[index % CATEGORY_PALETTE.length];

const formatDate = (iso: string) => {
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return '—';
  }
};

const getRelativeTime = (iso: string): string => {
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return mins <= 1 ? 'Just now' : `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days === 1) return 'Yesterday';
    if (days < 30) return `${days}d ago`;
    const months = Math.floor(days / 30);
    if (months < 12) return `${months}mo ago`;
    return `${Math.floor(months / 12)}y ago`;
  } catch {
    return '—';
  }
};

// ============================================================================
// ROLE AVATAR
// ============================================================================

const RoleAvatar: React.FC<{ role: CustomRole; size?: 'sm' | 'md' | 'lg' }> = ({ role, size = 'md' }) => {
  const colorClass = getAvatarColor(role.name);
  const sizeClass = size === 'sm' ? 'h-7 w-7 text-xs' : size === 'lg' ? 'h-14 w-14 text-xl' : 'h-9 w-9 text-sm';
  if (role.is_default) {
    return (
      <div className={cn('flex shrink-0 items-center justify-center rounded-full bg-primary/10', sizeClass)}>
        <Lock className={cn('text-primary', size === 'sm' ? 'h-3 w-3' : size === 'lg' ? 'h-6 w-6' : 'h-4 w-4')} />
      </div>
    );
  }
  return (
    <div className={cn('flex shrink-0 items-center justify-center rounded-full font-semibold', colorClass, sizeClass)}>
      {getRoleInitials(role.name)}
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const RoleManagementPage: React.FC = () => {
  const [roles, setRoles] = useState<CustomRole[]>([]);
  const [permissionsGrouped, setPermissionsGrouped] = useState<PermissionCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openPermissionDialog, setOpenPermissionDialog] = useState(false);
  const [selectedRole, setSelectedRole] = useState<CustomRole | null>(null);
  const [editingRole, setEditingRole] = useState<CustomRole | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState<CustomRole | null>(null);

  const [formData, setFormData] = useState<RoleFormData>({
    name: '',
    description: '',
    is_active: true,
  });

  useEffect(() => {
    fetchRoles();
    fetchPermissions();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- mount-only initial load

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const data = await customRolesAPI.getRoles();
      setRoles(data);
    } catch (error: any) {
      console.error('Error fetching roles:', error);
      if (error.response?.status === 403) {
        toast({
          title: 'Access Restricted',
          description: 'You do not have permission to view roles. Please contact your administrator.',
          variant: 'destructive',
        });
      } else {
        toast({ title: 'Error', description: formatErrorMessage(error) || 'Failed to fetch roles', variant: 'destructive' });
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchPermissions = async () => {
    try {
      const data = await permissionsAPI.getPermissionsGrouped();
      setPermissionsGrouped(data);
    } catch (error) {
      console.error('Error fetching permissions:', error);
    }
  };

  const formatErrorMessage = (error: any): string => {
    if (error.response?.data) {
      if (Array.isArray(error.response.data.detail)) {
        return error.response.data.detail.map((err: any) => err.msg || JSON.stringify(err)).join(', ');
      }
      if (typeof error.response.data.detail === 'string') return error.response.data.detail;
      if (error.response.data.message) return error.response.data.message;
    }
    return error.message || 'An unknown error occurred';
  };

  const handleCreateRole = async () => {
    try {
      await customRolesAPI.createRole({
        name: formData.name,
        description: formData.description || null,
        is_active: formData.is_active,
      });
      toast({ title: 'Role created', description: 'Role created successfully' });
      handleCloseDialog();
      fetchRoles();
    } catch (error: any) {
      console.error('Error creating role:', error);
      toast({ title: 'Error', description: formatErrorMessage(error) || 'Failed to create role', variant: 'destructive' });
    }
  };

  const handleUpdateRole = async () => {
    if (!editingRole) return;
    try {
      await customRolesAPI.updateRole(editingRole.id, {
        name: formData.name,
        description: formData.description || null,
        is_active: formData.is_active,
      });
      // sync selectedRole display if editing the currently selected one
      if (selectedRole?.id === editingRole.id) {
        setSelectedRole({ ...selectedRole, name: formData.name, description: formData.description || null, is_active: formData.is_active });
      }
      toast({ title: 'Role updated', description: 'Role updated successfully' });
      handleCloseDialog();
      fetchRoles();
    } catch (error: any) {
      console.error('Error updating role:', error);
      toast({ title: 'Error', description: formatErrorMessage(error) || 'Failed to update role', variant: 'destructive' });
    }
  };

  const handleDeleteRole = async () => {
    if (!roleToDelete) return;
    try {
      await customRolesAPI.deleteRole(roleToDelete.id);
      toast({ title: 'Role deleted', description: 'Role deleted successfully' });
      setDeleteConfirmOpen(false);
      if (selectedRole?.id === roleToDelete.id) setSelectedRole(null);
      setRoleToDelete(null);
      fetchRoles();
    } catch (error: any) {
      console.error('Error deleting role:', error);
      toast({ title: 'Error', description: formatErrorMessage(error) || 'Failed to delete role', variant: 'destructive' });
    }
  };

  const handleCloneRole = async (role: CustomRole) => {
    try {
      await customRolesAPI.cloneRole(role.id, `${role.name} (Copy)`);
      toast({ title: 'Role cloned', description: 'Role cloned successfully' });
      fetchRoles();
    } catch (error: any) {
      console.error('Error cloning role:', error);
      toast({ title: 'Error', description: formatErrorMessage(error) || 'Failed to clone role', variant: 'destructive' });
    }
  };

  const handleOpenCreateDialog = () => {
    setFormData({ name: '', description: '', is_active: true });
    setEditingRole(null);
    setOpenDialog(true);
  };

  const handleOpenEditDialog = (role: CustomRole) => {
    setFormData({ name: role.name, description: role.description || '', is_active: role.is_active });
    setEditingRole(role);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingRole(null);
    setFormData({ name: '', description: '', is_active: true });
  };

  const handleOpenPermissionDialog = (role: CustomRole) => {
    setSelectedRole(role);
    setOpenPermissionDialog(true);
  };

  const handleClosePermissionDialog = () => {
    setOpenPermissionDialog(false);
    fetchRoles();
  };

  const filteredRoles = roles.filter(
    (role) =>
      role.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (role.description?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false)
  );

  const systemRoles = filteredRoles.filter((r) => r.is_default);
  const customRoles = filteredRoles.filter((r) => !r.is_default);

  const stats = {
    total: roles.length,
    active: roles.filter((r) => r.is_active).length,
    totalUsers: roles.reduce((sum, r) => sum + r.user_count, 0),
    categories: permissionsGrouped.length,
  };

  // ============ RENDER ============

  const renderRoleItem = (role: CustomRole) => {
    const isSelected = selectedRole?.id === role.id;
    return (
      <button
        key={role.id}
        onClick={() => setSelectedRole(role)}
        className={cn(
          'group flex w-full items-center gap-3 border-b border-border/40 px-4 py-3 text-left transition-colors',
          isSelected
            ? 'border-l-[3px] border-l-primary bg-accent/80'
            : 'border-l-[3px] border-l-transparent hover:bg-muted/60'
        )}
      >
        <RoleAvatar role={role} size="sm" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-sm font-medium truncate text-foreground leading-tight">{role.name}</span>
            {!role.is_active && (
              <span className="text-[10px] text-muted-foreground shrink-0">(Inactive)</span>
            )}
          </div>
          <div className="flex items-center gap-3 mt-0.5">
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Users className="h-3 w-3" />{role.user_count}
            </span>
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <ShieldCheck className="h-3 w-3" />{role.permission_count}
            </span>
          </div>
        </div>
        <ChevronRight
          className={cn(
            'h-4 w-4 shrink-0 text-muted-foreground transition-opacity',
            isSelected ? 'opacity-80' : 'opacity-0 group-hover:opacity-50'
          )}
        />
      </button>
    );
  };

  return (
    <div className="flex flex-1 flex-col bg-muted/30 text-foreground">
      <div className="mx-auto flex w-full max-w-7xl flex-1 flex-col gap-4 px-3 py-4 sm:gap-5 sm:px-4 sm:py-6 lg:px-8">
        <Card className="overflow-hidden rounded-2xl border-border/60 shadow-sm">
          <div className="border-b border-border/60 bg-muted/25 px-5 pb-5 pt-6 sm:px-6 sm:pb-6 sm:pt-7">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Shield className="h-[1.35rem] w-[1.35rem]" />
                </div>
                <div className="min-w-0 space-y-1.5">
                  <h1 className="text-xl font-semibold tracking-tight sm:text-2xl">
                    Roles &amp; permissions
                  </h1>
                  <p className="max-w-2xl text-sm leading-relaxed text-muted-foreground">
                    Manage custom roles with granular permission control. Select
                    a role to review coverage, then use the matrix to edit
                    grants.
                  </p>
                </div>
              </div>
              <Button
                type="button"
                onClick={handleOpenCreateDialog}
                size="sm"
                className="shrink-0 gap-2 self-start sm:self-center"
              >
                <Plus className="h-4 w-4" />
                New role
              </Button>
            </div>
          </div>
          <CardContent className="p-0">
            <div className="grid grid-cols-2 gap-px border-t border-border/60 bg-border/35 lg:grid-cols-4">
              <div className="bg-card p-4 sm:p-5">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      Total roles
                    </p>
                    <p className="mt-1 text-2xl font-semibold tabular-nums tracking-tight">
                      {stats.total}
                    </p>
                  </div>
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                    <Shield className="h-4 w-4 text-primary" />
                  </div>
                </div>
              </div>
              <div className="bg-card p-4 sm:p-5">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      Active roles
                    </p>
                    <p className="mt-1 text-2xl font-semibold tabular-nums tracking-tight">
                      {stats.active}
                    </p>
                  </div>
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-emerald-500/15">
                    <CheckCircle className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                  </div>
                </div>
              </div>
              <div className="bg-card p-4 sm:p-5">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      Assigned users
                    </p>
                    <p className="mt-1 text-2xl font-semibold tabular-nums tracking-tight">
                      {stats.totalUsers}
                    </p>
                  </div>
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                    <Users className="h-4 w-4 text-primary" />
                  </div>
                </div>
              </div>
              <div className="bg-card p-4 sm:p-5">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      Permission categories
                    </p>
                    <p className="mt-1 text-2xl font-semibold tabular-nums tracking-tight">
                      {stats.categories}
                    </p>
                  </div>
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                    <ShieldCheck className="h-4 w-4 text-primary" />
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border-border/60 shadow-sm">
          <div className="flex min-h-[min(560px,calc(100vh-14rem))] flex-1 flex-col lg:min-h-[480px] lg:flex-row">
            <aside className="flex w-full flex-col border-b border-border/60 bg-muted/20 lg:w-[min(100%,288px)] lg:shrink-0 lg:border-b-0 lg:border-r lg:border-border/60">
              <div className="border-b border-border/60 px-4 py-3 sm:px-4 sm:py-3.5">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    All roles
                  </span>
                  <Badge variant="outline" className="h-5 px-1.5 text-xs font-normal">
                    {roles.length}
                  </Badge>
                </div>
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="Search roles…"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="h-9 pl-9 text-sm"
                  />
                </div>
              </div>

              <div className="min-h-[200px] flex-1 overflow-y-auto lg:min-h-0">
                {loading ? (
                  <div className="flex items-center justify-center py-16">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : filteredRoles.length === 0 ? (
                  <div className="flex flex-col items-center justify-center gap-3 px-4 py-12 text-center">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                      <Shield className="h-6 w-6 text-muted-foreground" />
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {searchTerm ? 'No roles match your search' : 'No roles yet'}
                    </p>
                    {!searchTerm && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-1.5"
                        onClick={handleOpenCreateDialog}
                      >
                        <Plus className="h-4 w-4" />
                        Create role
                      </Button>
                    )}
                  </div>
                ) : (
                  <>
                    {systemRoles.length > 0 && (
                      <div>
                        <div className="flex items-center gap-1.5 px-4 py-2">
                          <Lock className="h-3.5 w-3.5 text-muted-foreground" />
                          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                            System
                          </span>
                        </div>
                        {systemRoles.map(renderRoleItem)}
                      </div>
                    )}
                    {customRoles.length > 0 && (
                      <div>
                        <div className="mt-1 flex items-center gap-1.5 px-4 py-2">
                          <Shield className="h-3.5 w-3.5 text-muted-foreground" />
                          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                            Custom
                          </span>
                        </div>
                        {customRoles.map(renderRoleItem)}
                      </div>
                    )}
                  </>
                )}
              </div>

              {!loading && filteredRoles.length > 0 && (
                <div className="border-t border-border/60 bg-muted/25 px-4 py-2.5">
                  <p className="text-[11px] text-muted-foreground">
                    {filteredRoles.length} role{filteredRoles.length !== 1 ? 's' : ''}
                    {searchTerm ? ` matching “${searchTerm}”` : ''}
                  </p>
                </div>
              )}
            </aside>

            <div className="flex min-h-[280px] flex-1 flex-col overflow-hidden bg-card lg:min-h-0">
              {!selectedRole ? (
                <div className="flex flex-1 flex-col items-center justify-center gap-5 px-6 py-12 text-center sm:py-16">
                  <div className="relative">
                    <div className="flex h-20 w-20 items-center justify-center rounded-2xl border border-border/60 bg-muted/40 shadow-inner">
                      <ShieldCheck className="h-10 w-10 text-primary/50" />
                    </div>
                    <div className="absolute -bottom-1 -right-1 flex h-7 w-7 items-center justify-center rounded-full border border-border/60 bg-card shadow-sm">
                      <Key className="h-3.5 w-3.5 text-muted-foreground" />
                    </div>
                  </div>
                  <div className="max-w-sm space-y-2">
                    <p className="text-base font-semibold text-foreground">
                      Select a role
                    </p>
                    <p className="text-sm leading-relaxed text-muted-foreground">
                      Choose a role from the list to see description, permission
                      coverage, assignments, and shortcuts to edit or clone.
                    </p>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    className="gap-2"
                    onClick={handleOpenCreateDialog}
                  >
                    <Plus className="h-4 w-4" />
                    Create new role
                  </Button>
                </div>
              ) : (
                <div className="flex h-full flex-col overflow-y-auto">
                  <div className="border-b border-border/60 bg-gradient-to-b from-muted/40 to-card px-5 py-6 sm:px-8">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
                      <RoleAvatar role={selectedRole} size="lg" />
                      <div className="min-w-0 flex-1 pt-0.5 sm:pt-1">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                          <div className="min-w-0 space-y-1.5">
                            <div className="flex flex-wrap items-center gap-2">
                              <h2 className="text-xl font-bold leading-tight tracking-tight sm:text-2xl">
                                {selectedRole.name}
                              </h2>
                              {selectedRole.is_default && (
                                <Badge
                                  variant="outline"
                                  className="shrink-0 border-primary/30 bg-primary/5 text-xs text-primary"
                                >
                                  <Lock className="mr-1 h-2.5 w-2.5" />
                                  System
                                </Badge>
                              )}
                              <Badge
                                variant="outline"
                                className={cn(
                                  'shrink-0 text-xs',
                                  selectedRole.is_active
                                    ? 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-400'
                                    : 'border-border text-muted-foreground'
                                )}
                              >
                                <span
                                  className={cn(
                                    'mr-1.5 inline-block h-1.5 w-1.5 rounded-full',
                                    selectedRole.is_active
                                      ? 'bg-emerald-500'
                                      : 'bg-muted-foreground'
                                  )}
                                />
                                {selectedRole.is_active ? 'Active' : 'Inactive'}
                              </Badge>
                            </div>
                            <p className="line-clamp-2 text-sm leading-relaxed text-muted-foreground">
                              {selectedRole.description ||
                                'No description provided for this role.'}
                            </p>
                          </div>

                          {!selectedRole.is_default && (
                            <div className="flex shrink-0 flex-wrap items-center gap-1.5 sm:justify-end">
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-8 gap-1.5 text-xs"
                                onClick={() => handleCloneRole(selectedRole)}
                              >
                                <Copy className="h-3.5 w-3.5" />
                                Clone
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-8 gap-1.5 text-xs"
                                onClick={() => handleOpenEditDialog(selectedRole)}
                              >
                                <Pencil className="h-3.5 w-3.5" />
                                Edit
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-8 text-destructive hover:bg-destructive/5 hover:text-destructive border-destructive/20 text-xs"
                                onClick={() => {
                                  setRoleToDelete(selectedRole);
                                  setDeleteConfirmOpen(true);
                                }}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {(() => {
                    const totalAvailablePerms = permissionsGrouped.reduce(
                      (sum, cat) => sum + (cat.permissions?.length ?? 0),
                      0
                    );
                    const coveragePct =
                      totalAvailablePerms > 0
                        ? Math.round(
                            (selectedRole.permission_count / totalAvailablePerms) *
                              100
                          )
                        : 0;
                    const coverageColor =
                      coveragePct >= 70
                        ? 'bg-emerald-500'
                        : coveragePct >= 35
                          ? 'bg-primary'
                          : 'bg-amber-500';
                    const coverageTextColor =
                      coveragePct >= 70
                        ? 'text-emerald-600 dark:text-emerald-400'
                        : coveragePct >= 35
                          ? 'text-primary'
                          : 'text-amber-600 dark:text-amber-400';
                    const MAX_DOTS = 6;
                    const dotsToShow = Math.min(selectedRole.user_count, MAX_DOTS);
                    const overflow = selectedRole.user_count - MAX_DOTS;
                    return (
                      <div className="grid grid-cols-1 gap-3 border-b border-border/60 px-5 py-4 sm:grid-cols-3 sm:gap-4 sm:px-6 sm:py-5">
                        <Card className="rounded-xl border-border/60 bg-card shadow-sm">
                          <CardContent className="flex flex-col gap-2.5 p-4 sm:p-5">
                            <div className="flex items-center gap-1.5">
                              <div className="flex h-5 w-5 items-center justify-center rounded-md bg-primary/15">
                                <ShieldCheck className="h-3 w-3 text-primary" />
                              </div>
                              <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                                Permissions
                              </span>
                            </div>
                            <div>
                              <span className="text-3xl font-bold tabular-nums leading-none text-primary">
                                {selectedRole.permission_count}
                              </span>
                              {totalAvailablePerms > 0 && (
                                <span className="ml-1.5 text-xs text-muted-foreground">
                                  / {totalAvailablePerms}
                                </span>
                              )}
                            </div>
                            {totalAvailablePerms > 0 && (
                              <>
                                <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                                  <div
                                    className={cn(
                                      'h-full rounded-full transition-all duration-500',
                                      coverageColor
                                    )}
                                    style={{ width: `${coveragePct}%` }}
                                  />
                                </div>
                                <p
                                  className={cn(
                                    'text-xs font-semibold leading-none',
                                    coverageTextColor
                                  )}
                                >
                                  {coveragePct}% coverage
                                  <span className="ml-1 font-normal text-muted-foreground">
                                    · {permissionsGrouped.length} categories
                                  </span>
                                </p>
                              </>
                            )}
                          </CardContent>
                        </Card>

                        <Card className="rounded-xl border-border/60 bg-card shadow-sm">
                          <CardContent className="flex flex-col gap-2.5 p-4 sm:p-5">
                            <div className="flex items-center gap-1.5">
                              <div className="flex h-5 w-5 items-center justify-center rounded-md bg-blue-500/15 dark:bg-blue-950">
                                <Users className="h-3 w-3 text-blue-600 dark:text-blue-400" />
                              </div>
                              <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                                Assigned users
                              </span>
                            </div>
                            <span className="text-3xl font-bold tabular-nums leading-none text-foreground">
                              {selectedRole.user_count}
                            </span>
                            {selectedRole.user_count === 0 ? (
                              <p className="text-xs leading-none text-muted-foreground">
                                No users assigned yet
                              </p>
                            ) : (
                              <>
                                <div className="flex items-center gap-0.5">
                                  {Array.from({ length: dotsToShow }).map((_, i) => (
                                    <div
                                      key={i}
                                      className="flex h-5 w-5 items-center justify-center rounded-full border-2 border-card text-[9px] font-bold"
                                      style={{
                                        marginLeft: i > 0 ? '-6px' : 0,
                                        backgroundColor: AVATAR_COLORS[i % AVATAR_COLORS.length]
                                          .split(' ')[0]
                                          .replace('bg-', ''),
                                        background: `hsl(var(--primary) / ${0.15 + i * 0.08})`,
                                        color: `hsl(var(--primary))`,
                                        zIndex: dotsToShow - i,
                                      }}
                                    />
                                  ))}
                                  {overflow > 0 && (
                                    <span className="ml-2 text-[10px] text-muted-foreground">
                                      +{overflow} more
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs leading-none text-muted-foreground">
                                  {selectedRole.user_count === 1
                                    ? '1 member with this role'
                                    : `${selectedRole.user_count} members with this role`}
                                </p>
                              </>
                            )}
                          </CardContent>
                        </Card>

                        <Card className="rounded-xl border-border/60 bg-card shadow-sm">
                          <CardContent className="flex flex-col gap-2.5 p-4 sm:p-5">
                            <div className="flex items-center gap-1.5">
                              <div className="flex h-5 w-5 items-center justify-center rounded-md bg-muted">
                                <CalendarDays className="h-3 w-3 text-muted-foreground" />
                              </div>
                              <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                                Created
                              </span>
                            </div>
                            <span
                              className="text-3xl font-bold tabular-nums leading-none tracking-tight text-foreground"
                              style={{ fontSize: '1.35rem' }}
                            >
                              {getRelativeTime(selectedRole.created_at)}
                            </span>
                            <p className="text-xs leading-none text-muted-foreground">
                              {formatDate(selectedRole.created_at)}
                            </p>
                            <p className="flex items-center gap-1 text-xs leading-none text-muted-foreground">
                              <Activity className="h-2.5 w-2.5" />
                              Updated {getRelativeTime(selectedRole.updated_at)}
                            </p>
                          </CardContent>
                        </Card>
                      </div>
                    );
                  })()}

                  <div className="flex flex-1 flex-col px-5 py-6 sm:px-8">
                    <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                      <div>
                        <h3 className="text-sm font-semibold text-foreground">
                          Permission scope
                        </h3>
                        <p className="mt-0.5 text-xs text-muted-foreground">
                          Platform capabilities available to this role
                        </p>
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        className="shrink-0 gap-2 self-start sm:self-auto"
                        onClick={() => handleOpenPermissionDialog(selectedRole)}
                      >
                        <ShieldCheck className="h-3.5 w-3.5" />
                        Manage permissions
                      </Button>
                    </div>

                    {permissionsGrouped.length === 0 ? (
                      <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Loading categories…
                      </div>
                    ) : (
                      <div className="overflow-hidden rounded-xl border border-border/50 bg-muted/20 p-2 sm:p-3">
                        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
                          {permissionsGrouped.map((cat, i) => {
                            const Icon = getCategoryIcon(cat.category);
                            const palette = getCategoryPalette(i);
                            return (
                              <div
                                key={cat.category}
                                className={cn(
                                  'flex items-center gap-2 rounded-lg border px-3 py-2.5 transition-colors hover:opacity-90',
                                  palette
                                )}
                              >
                                <Icon className="h-3.5 w-3.5 shrink-0" />
                                <span className="truncate text-xs font-medium">
                                  {cat.category_display_name}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {selectedRole.is_default && (
                      <div className="mt-5 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50/80 px-4 py-3 dark:border-amber-800/50 dark:bg-amber-950/30">
                        <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900">
                          <Info className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-amber-800 dark:text-amber-300">
                            System role
                          </p>
                          <p className="mt-0.5 text-xs leading-relaxed text-amber-700 dark:text-amber-400">
                            This role is built-in and cannot be renamed or deleted.
                            You can still configure its permissions using Manage
                            permissions above.
                          </p>
                        </div>
                      </div>
                    )}

                    <p className="mt-6 flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Activity className="h-3 w-3" />
                      Last modified {formatDate(selectedRole.updated_at)}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </Card>
      </div>

      {/* ── Create / Edit Dialog ── */}
      <Dialog
        open={openDialog}
        onOpenChange={(open) => {
          if (!open) handleCloseDialog();
        }}
      >
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10">
                <Shield className="h-5 w-5 text-primary" />
              </div>
              <DialogTitle>{editingRole ? 'Edit Role' : 'Create New Role'}</DialogTitle>
            </div>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-1.5">
              <Label htmlFor="role-name" className="text-sm font-medium">
                Role Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="role-name"
                autoFocus
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g. Support Manager"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="role-description" className="text-sm font-medium">Description</Label>
              <textarea
                id="role-description"
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe this role's purpose and responsibilities"
              />
            </div>
            <div className="flex items-center justify-between rounded-lg border border-border/60 bg-muted/20 px-4 py-3">
              <div>
                <Label htmlFor="role-active" className="cursor-pointer text-sm font-medium">Active</Label>
                <p className="text-xs text-muted-foreground mt-0.5">Inactive roles can't be assigned to new users</p>
              </div>
              <Switch
                id="role-active"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseDialog}>Cancel</Button>
            <Button
              onClick={editingRole ? handleUpdateRole : handleCreateRole}
              disabled={!formData.name.trim()}
            >
              {editingRole ? 'Update Role' : 'Create Role'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Permission Matrix Dialog ── */}
      {openPermissionDialog && selectedRole && (
        <PermissionMatrixDialog
          open={openPermissionDialog}
          role={selectedRole}
          onClose={handleClosePermissionDialog}
        />
      )}

      {/* ── Delete Confirmation ── */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Role</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>"{roleToDelete?.name}"</strong>? This action cannot be undone.
              {roleToDelete && roleToDelete.user_count > 0 && (
                <span className="flex items-start gap-2 mt-3 rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-800/50 dark:bg-amber-950/30 px-3 py-2 text-amber-700 dark:text-amber-400">
                  <Info className="h-4 w-4 shrink-0 mt-0.5" />
                  <span>{roleToDelete.user_count} user(s) have this role assigned. They will revert to their base role.</span>
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => { setDeleteConfirmOpen(false); setRoleToDelete(null); }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDeleteRole}
            >
              Delete Role
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default RoleManagementPage;
